import { defineComponent as _defineComponent } from 'vue'
import { ImageResizerOptions, ImageResizerSupportedFormats, getImageUrlWithImageResizerParams, ImageResizerModes } from '../util/imageResizer';
import { useWebpFeatureCheck } from '../util/useWebpFeatureCheck';
import { computed } from 'vue';
import ImageWithAutoSizes from './ImageWithAutoSizes.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'AutoOptimizedImage',
  props: {
    baseUrl: null,
    altText: { default: '' },
    className: { default: '' },
    lazyload: { type: Boolean, default: false },
    lossless: { type: Boolean },
    hasTransparency: { type: Boolean },
    imageResizerOptions: { default: undefined },
    imageResizerOptionsLegacy: { default: undefined },
    imageResizerMode: { default: undefined },
    widths: null,
    widthsLegacy: null,
    aspectRatio: { default: undefined }
  },
  emits: ['resize', 'loaded', 'error', 'click'],
  setup(__props: any, { emit }) {

const props = __props as { baseUrl: string, altText: string, className: string, lazyload: boolean, lossless: boolean, hasTransparency: boolean, imageResizerOptions: ImageResizerOptions, imageResizerOptionsLegacy: ImageResizerOptions, imageResizerMode: ImageResizerModes, widths: number[], widthsLegacy: number[], aspectRatio: number }





const { isWebpLossySupported, isWebpLosslessSupported, isWebpAlphaSupported, areAllWebpFeaturesSupported, areWebpChecksCompleted } = useWebpFeatureCheck();

const supportedFormat = computed<ImageResizerSupportedFormats | undefined>(() => {
    if (!areWebpChecksCompleted.value) {
        return undefined;
    }
    if (areAllWebpFeaturesSupported.value ||
        (!props.lossless && isWebpLossySupported.value) ||
        (props.lossless && isWebpLosslessSupported.value && (!props.hasTransparency || isWebpAlphaSupported.value))
    ) {
        return 'webp';
    }
    return (props.lossless || props.hasTransparency ? 'png' : 'jpeg');
});

const isModernFormatSupported = computed(() => {
    return (supportedFormat.value === 'webp');
});

const format = computed<ImageResizerSupportedFormats | undefined>(() => {
    if ((props.imageResizerOptionsLegacy && props.imageResizerOptionsLegacy.format) && (!isModernFormatSupported.value)) {
        return props.imageResizerOptionsLegacy.format;
    }
    if (props.imageResizerOptions && props.imageResizerOptions.format) {
        return props.imageResizerOptions.format;
    }
    return supportedFormat.value;
});

const isModernFormat = computed(() => {
    return (format.value === 'webp');
});

const urlBuilder = (width: number) => {
    const imageResizerOptions:ImageResizerOptions = {};

    imageResizerOptions.width = width;
    if (props.aspectRatio) {
        imageResizerOptions.height = Math.round(width / props.aspectRatio);
    }
    if (props.imageResizerMode) {
        imageResizerOptions.mode = props.imageResizerMode;
    }

    // merge options. The supplied options overrides the calculated. legacy overrides modern (only when modern format isnt supported)
    let mergedOptions = { ...imageResizerOptions, ...props.imageResizerOptions };
    if (!isModernFormatSupported.value) {
        mergedOptions = { ...mergedOptions, ...props.imageResizerOptionsLegacy };
    }
    mergedOptions.format = format.value;

    /*
    Things get a bit tricky here
    Lets say we have:
    {
        lossless: false
        imageResizerOptionsLegacy = {'format': 'png'}
    }
    lossless:false would usually generate a quality parameter. But we do not want that for the png.
    So we will need to make sure its not going to be there.
    This can be achieved by setting "quality" after the merge

    However, consider this:
    {
        lossless: true
        imageResizerOptionsLegacy = {'quality': 50}
    }
    In this case, we do not want to override quality with the default quality after the merge

    The solution to this is to:
    1) Sanitize the merged options, making sure that it does not contain any irrelevant options
    2) Only override options such as "quality" with the default if its not in the merged options
    */

    if (format.value === 'webp') {
        mergedOptions.lossless = props.lossless;
        if (mergedOptions.noalpha === undefined) {
            if (!props.hasTransparency) {
                mergedOptions.noalpha = true;
            }
        }
        if (props.lossless) {
            mergedOptions.quality = undefined;
        } else {
            if (mergedOptions.quality === undefined) {
                mergedOptions.quality = 90;
            }
        }
    } else {
        mergedOptions.noalpha = undefined;
        mergedOptions.lossless = undefined;
        if (format.value === 'png') {
            mergedOptions.quality = undefined;
        }
        if (format.value === 'jpeg') {
            if (mergedOptions.quality === undefined) {
                mergedOptions.quality = 90;
            }
        }
    }
    return getImageUrlWithImageResizerParams(props.baseUrl, mergedOptions);
};

const srcsetWidths = computed(() => {
    if (format.value === undefined) {
        return [];
    }
    return (isModernFormat.value ? props.widths : props.widthsLegacy);
});

const srcset = computed(() => {
    return srcsetWidths.value.map(width => {
        return urlBuilder(width) + ` ${width}w`;
    }).join(', ');
});

const src = computed(() => {
    if (srcsetWidths.value && srcsetWidths.value.length > 0) {
        const largestWidthInSet = srcsetWidths.value[srcsetWidths.value.length - 1];
        return urlBuilder(largestWidthInSet);
    } else {
        return '';
    }
});

return { __sfc: true,emit, props, isWebpLossySupported, isWebpLosslessSupported, isWebpAlphaSupported, areAllWebpFeaturesSupported, areWebpChecksCompleted, supportedFormat, isModernFormatSupported, format, isModernFormat, urlBuilder, srcsetWidths, srcset, src, ImageWithAutoSizes }
}

})